import React from 'react';
import { formatDate } from '../utils/dateConverter';
import { LOAD_LABEL, loadState, overbookedBy, weekColumns, weekLabel } from '../utils/planning';

const fmt = (hours) => {
    const value = Number(hours) || 0;
    if (value === 0) return '–';
    return Number.isInteger(value) ? `${value} h` : `${value.toFixed(1).replace('.', ',')} h`;
};

/** Weeks across, people down: planned hours against each person's capacity. */
export default function WorkloadGrid({ workload, onCellClick }) {
    const users = workload?.users || [];
    const weeks = weekColumns(users);

    if (!users.length) {
        return <p className="pl-empty">Keine aktiven Mitarbeiter in diesem Zeitraum.</p>;
    }

    return (
        <div className="pl-workload-wrap">
            <table className="pl-workload">
                <thead>
                    <tr>
                        <th className="pl-wl-name">Mitarbeiter</th>
                        {weeks.map(week => (
                            <th key={`${week.year}-${week.iso_week}`} className="pl-wl-week">
                                <span className="pl-wl-kw">{weekLabel(week)}</span>
                                <span className="pl-wl-from">{formatDate(week.week_start)}</span>
                            </th>
                        ))}
                        <th className="pl-wl-total">Summe</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map(user => {
                        const byWeek = new Map(
                            (user.weeks || []).map(w => [`${w.year}-${w.iso_week}`, w])
                        );
                        return (
                            <tr key={user.user_id}>
                                <th scope="row" className="pl-wl-name">
                                    {user.name || user.username}
                                </th>
                                {weeks.map(column => {
                                    const key = `${column.year}-${column.iso_week}`;
                                    const week = byWeek.get(key);
                                    const planned = week?.planned_hours || 0;
                                    const capacity = week?.capacity_hours || 0;
                                    const state = loadState(planned, capacity);
                                    const over = overbookedBy(planned, capacity);
                                    return (
                                        <td
                                            key={key}
                                            className={`pl-wl-cell pl-wl-cell--${state}`}
                                            onClick={() => onCellClick && onCellClick(user, week)}
                                            title={[
                                                `${user.name || user.username}, ${weekLabel(column)}`,
                                                `Kapazität ${fmt(capacity)}`,
                                                `geplant ${fmt(planned)}`,
                                                week?.absence_days
                                                    ? `${week.absence_days} Tag(e) abwesend`
                                                    : null,
                                                LOAD_LABEL[state],
                                            ].filter(Boolean).join(' · ')}
                                        >
                                            <span className="pl-wl-planned">{fmt(planned)}</span>
                                            <span className="pl-wl-capacity">/ {fmt(capacity)}</span>
                                            {over > 0 && (
                                                <span className="pl-wl-over">+{fmt(over)}</span>
                                            )}
                                            {!over && week?.absence_days > 0 && (
                                                <span className="pl-wl-absence">
                                                    {week.absence_days} Tag{week.absence_days === 1 ? '' : 'e'} frei
                                                </span>
                                            )}
                                        </td>
                                    );
                                })}
                                <td className="pl-wl-total">
                                    <span className="pl-wl-planned">{fmt(user.total_planned_hours)}</span>
                                    <span className="pl-wl-capacity">/ {fmt(user.total_capacity_hours)}</span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <p className="pl-legend">
                <span className="pl-dot pl-wl-cell--over" /> überbucht
                <span className="pl-dot pl-wl-cell--full" /> ausgelastet
                <span className="pl-dot pl-wl-cell--partial" /> frei
                <span className="pl-dot pl-wl-cell--off" /> abwesend
                <span className="pl-legend-note">
                    Kapazität = vertragliche Stunden abzüglich Feiertagen, Krankheit und
                    genehmigter Abwesenheit.
                </span>
            </p>
        </div>
    );
}
